<template>
  <dialog :class="['modal', { 'modal-open': true }]">
    <div class="modal-box w-[calc(100%-32px)] max-w-[540px] mx-4 sm:mx-auto p-5 sm:p-[30px] rounded-[20px] bg-white border border-[#22ec9b] relative font-['Raleway',sans-serif] shadow-2xl">
      <form method="dialog">
        <button
          class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 text-[#5b5c60]"
          :disabled="isSubmitting"
          @click.prevent="!isSubmitting && $emit('close')"
        >
          ✕
        </button>
      </form>

      <!-- Header -->
      <div class="flex items-center gap-[10px] mb-[12px]">
        <div class="w-[36px] h-[36px] rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <svg
            class="w-[18px] h-[18px] text-[#dc2626]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </div>
        <div>
          <h3 class="font-bold text-[18px] text-[#043f42] m-0">
            {{ isSuccess ? 'Deletion Complete' : `Delete ${count} Selected ${itemLabel}${count > 1 ? 's' : ''}` }}
          </h3>
          <p class="text-[12px] text-[#5b5c60] m-0">
            This action cannot be undone.
          </p>
        </div>
      </div>

      <div class="w-full h-[1px] bg-[#043f421a] my-[14px]" />

      <!-- Pre-Deletion Item List -->
      <div
        v-if="!isSubmitting && !isSuccess"
        class="flex flex-col gap-[10px]"
      >
        <p class="text-[14px] text-[#5b5c60] leading-relaxed m-0">
          Are you sure you want to delete these <strong>{{ count }}</strong> {{ itemLabelLower }}{{ count > 1 ? 's' : '' }}?
        </p>

        <!-- Selected Items Pills -->
        <div class="flex flex-wrap gap-[6px] max-h-[120px] overflow-y-auto p-[10px] bg-gray-50 rounded-[12px] border border-gray-200">
          <span
            v-for="c in cohortList"
            :key="c.id"
            class="inline-flex items-center gap-[4px] px-[10px] py-[4px] bg-white border border-gray-300 rounded-full text-[12px] font-semibold text-[#043f42]"
          >
            {{ c.name || `${itemLabel} #${c.id}` }}
          </span>
        </div>
      </div>

      <!-- Live Deletion Progress State -->
      <div
        v-if="isSubmitting || isSuccess"
        class="flex flex-col gap-[14px] py-[10px]"
      >
        <!-- Progress Bar with Percentage -->
        <div class="flex justify-between items-center text-[13px] font-semibold text-[#043f42]">
          <span>Progress</span>
          <span>{{ progressPercentage }}%</span>
        </div>
        <div class="w-full h-[8px] bg-gray-100 rounded-full overflow-hidden">
          <div
            class="h-full transition-all duration-300 ease-out"
            :class="isSuccess ? 'bg-[#01d680]' : 'bg-[#dc2626]'"
            :style="{ width: `${progressPercentage}%` }"
          />
        </div>

        <!-- Live Step Details -->
        <div class="flex items-center justify-between text-[13px]">
          <div class="flex items-center gap-[8px]">
            <span
              v-if="!isSuccess"
              class="loading loading-spinner loading-xs text-[#dc2626]"
            />
            <svg
              v-else
              class="w-[16px] h-[16px] text-[#01d680]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span :class="isSuccess ? 'text-[#00721b] font-bold' : 'text-[#5b5c60] font-medium'">
              {{ statusMessage }}
            </span>
          </div>
          <span class="text-[12px] text-[#5b5c60]">
            {{ completedCount }} / {{ count }}
          </span>
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="error"
        class="text-red-600 text-[13px] mt-[12px] p-[10px] bg-red-50 rounded-[10px] border border-red-200"
      >
        {{ error }}
      </div>

      <!-- Action Buttons -->
      <div class="modal-action mt-[20px]">
        <button
          v-if="!isSuccess"
          type="button"
          class="btn btn-ghost rounded-[38px] text-black font-['Raleway',sans-serif] font-bold"
          :disabled="isSubmitting"
          @click.prevent="$emit('close')"
        >
          Cancel
        </button>
        <button
          v-if="!isSuccess"
          type="button"
          class="btn rounded-[38px] bg-[#dc2626] hover:bg-red-700 border-none text-white font-['Raleway',sans-serif] font-bold"
          :disabled="isSubmitting"
          @click.prevent="startProgressiveDeletion"
        >
          <span
            v-if="isSubmitting"
            class="loading loading-spinner loading-sm"
          />
          {{ isSubmitting ? `Deleting (${completedCount}/${count})...` : `Delete (${count})` }}
        </button>
        <button
          v-if="isSuccess"
          type="button"
          class="btn rounded-[38px] bg-[#01d680] hover:bg-[#00c270] border-none text-[#043f42] font-['Raleway',sans-serif] font-bold w-full"
          @click.prevent="finishAndReload"
        >
          Done
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop bg-[#00000099]"
    >
      <button
        :disabled="isSubmitting"
        @click.prevent="!isSubmitting && $emit('close')"
      >
        close
      </button>
    </form>
  </dialog>
</template>

<script>
export default {
  name: 'BulkDeleteCohortModal',
  props: {
    cohortIds: { type: Array, required: true },
    cohorts: { type: Array, default: () => [] },
    itemType: { type: String, default: 'cohort' },
    endpoint: { type: String, default: '/cohorts/bulk_destroy.json' }
  },
  emits: ['close', 'deleted'],
  data () {
    return {
      isSubmitting: false,
      isSuccess: false,
      completedCount: 0,
      currentCohortName: '',
      error: null
    }
  },
  computed: {
    itemLabel () {
      return this.itemType === 'template' ? 'Cohort Template' : 'Cohort'
    },
    itemLabelLower () {
      return this.itemType === 'template' ? 'cohort template' : 'cohort'
    },
    count () {
      return (this.cohortIds || []).length
    },
    cohortList () {
      if (this.cohorts && this.cohorts.length > 0) {
        return this.cohorts.filter(c => this.cohortIds.includes(c.id))
      }
      return this.cohortIds.map(id => ({ id, name: `${this.itemLabel} #${id}` }))
    },
    progressPercentage () {
      if (this.count === 0) return 0
      return Math.round((this.completedCount / this.count) * 100)
    },
    statusMessage () {
      if (this.isSuccess) {
        return `Successfully deleted ${this.count} ${this.itemLabelLower}${this.count > 1 ? 's' : ''}.`
      }
      return `Deleting ${this.count} ${this.itemLabelLower}${this.count > 1 ? 's' : ''}...`
    }
  },
  methods: {
    async startProgressiveDeletion () {
      this.isSubmitting = true
      this.error = null
      this.completedCount = 0

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ ids: this.cohortIds })
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok || data.success === false) {
          throw new Error(data.error || `Failed to delete selected ${this.itemLabelLower}s.`)
        }

        this.completedCount = this.count
        this.isSuccess = true
        setTimeout(() => {
          this.finishAndReload()
        }, 800)
      } catch (err) {
        console.error('Bulk deletion error:', err)
        this.error = err.message || 'An error occurred during deletion. Please try again.'
        this.isSubmitting = false
      }
    },
    finishAndReload () {
      this.$emit('deleted', this.cohortIds)
      window.location.reload()
    }
  }
}
</script>
